/**
 * OrganizatorPanelPage.jsx — Organizatör portalı: kendi fuarının takip ve yönetimi (backlog #321 / #317,
 * 2026-10-11; plan: dev/firma-portal/initial_plan.txt).
 *
 * Organizatör (firma_to_fuar tip 2) yalnızca KENDİ fuarında tüm standları, firmaları, talepleri,
 * projeleri ve personelin belge durumlarını görür (backend #316, /portal/organizator/{fuarId}/...).
 * Görünümün kendisi SAKA'nın açtığı bir ayar (FUAR_GORUNUMU); kapalıysa sayfa yalnızca bunu söyler.
 *
 * Eylemler portal bağlamının `eylemler` listesinden: her biri için firma o fuarda organizatör, girişin
 * rolünde portal yetkisi ve SAKA'nın firma ayarı gerekir. Kapalı olan düğme hiç çizilmez; backend aynı
 * kuralı uçlarda uygular, burası yalnızca gösterir.
 *   - Standlar: ekle / düzenle / sil (STAND_*), borç (BORC_YONET)
 *   - Firmalar: ekle (FIRMA_EKLE), fuara / standa eşle (FIRMA_ESLE), fuardan çıkar (FIRMA_SIL)
 *   - Talepler: listele, standa talep ekle (TALEP_EKLE → standın talep sayfası), iptal (TALEP_SIL) —
 *     organizatör sergileyen gibi talep OLUŞTURMAZ, standın adına ekler (#317 kararı)
 *   - Projeler: yalnızca okur (plan: "proje işlemleri yapamayacak")
 *   - Personel: yalnızca durumlar — dosya, TC, fotoğraf, iletişim yok (KVKK, #316 kararı)
 *
 * #446 / #448 / #450 (2026-10-17): görünüm ARTIK TEK ANAHTAR DEĞİL, ALAN BAŞINA KADEME (Standlar, Firmalar, Talepler ve borç,
 * Projeler, Kişiler, Salonlar): Yok < İzleme (liste ve durumlar, hiçbir şeyi değiştirmez) < Denetim (İzleme + SAKA'nın açtığı eylemler).
 * Her alan kendi sayfası ve menü girişidir (menüde "Organizatör" üst başlığının altında); sayfa yalnızca İzleme kademesindeyse EKLE / DÜZENLE /
 * SİL / İPTAL düğmelerini ve kapalı eylem satırlarını hiç çizmez (`salt`). Eskiden tek sayfa ve beş sekme vardı; /Firma/Organizator ilk açık alana yönlenir.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
// WAS (until #450): ... ModalHeader, Nav, NavItem, NavLink, Pagination, ... -- the tabs are gone, one page per area
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Col, Container, Input, Label, Modal, ModalBody,
  ModalFooter, ModalHeader, Pagination, PaginationItem, PaginationLink, Row,
  Spinner, Table,
} from "reactstrap";
import { Megaphone, Plus, Pencil, Trash2, Wallet, Ban, Link2, Lock, History as GecmisIkonu } from "lucide-react";
import axios from "axios";
import { usePortal } from "../../Components/Contexts/PortalContext";
import { JAVA_BACKEND_API_URL, getAuthHeader } from "../../Components/Contexts/BackendConstants";
import {
  getOrganizatorStandlar, getOrganizatorFirmalar, getOrganizatorTalepler, getOrganizatorProjeler,
  getOrganizatorKisiler, talepIptalEt, teknikTalepGeriCek, setStandBorc, ORGANIZATOR_AYAR, ORGANIZATOR_ALAN,
} from "../../services/portalService";
import { BORC_DURUMU_STATUS } from "../../services/standTalepService";
import { isStandAdiCakismasi } from "../../services/standNaming";
import FirmaSearchSelect from "../../Components/Firma/FirmaSearchSelect";
import CreateFirmaModal from "../../Components/Firma/CreateFirmaModal";
// #456 / #458 (2026-10-17): the request filters (URL-synchronised), the payment link card and the kW notice helpers
import TalepFiltreleri from "../../Components/Talep/TalepFiltreleri.jsx"; // .jsx written out: on a case-insensitive filesystem the bare name also matches talepFiltreleri.js (the helper, no default export)
import OdemeBaglantisiKarti from "../../Components/Talep/OdemeBaglantisiKarti";
// #453 (2026-10-17): the history of a stand's requests (backend #452), in a modal from the row
import TalepGecmisi from "../../Components/Talep/TalepGecmisi.jsx"; // .jsx written out: on a case-insensitive filesystem the bare name also matches talepGecmisi.js (the helper, no default export)
import { useTalepFiltreleri } from "../../Components/Talep/talepFiltreleri";
import { kwAsimi, kwYazi } from "../../Components/Talep/kwKontrol";
// #462 (2026-10-20): who entered a request (backend #454): a Source column on the Talepler list
import TalepKaynagi from "../../Components/Talep/TalepKaynagi.jsx"; // .jsx written out: on a case-insensitive filesystem the bare name could also match a .js helper
import { standTalepleri, talepKaynaklari } from "../../Components/Talep/talepKaynaklari";
// #500 (2026-10-19): the header button of the Talepler page that opens "Stands Above the Free kW" (route /Firma/Organizator/SerbestKwAsimi)
import SerbestKwDugmesi from "../../Components/Talep/SerbestKwDugmesi";
import { currentLocaleTag } from "../../i18n";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const SAYFA = 20;
const hataMetni = (e) => e?.response?.data?.message || (typeof e?.response?.data === "string" ? e.response.data : null)
  || e?.message || i18n.t("portal:organizator.islemBasarisizOldu");
const api = (method, url, data) => axios({ method, url: `${JAVA_BACKEND_API_URL}${url}`, data, headers: getAuthHeader() })
  .then((r) => r.data);

const BORC_KATEGORILERI = [
  { key: "alan", get label() { return i18n.t("portal:organizator.alan"); } }, { key: "teknik", get label() { return i18n.t("portal:organizator.teknik"); } },
  { key: "ekipman", get label() { return i18n.t("portal:organizator.ekipman"); } }, { key: "hizmet", get label() { return i18n.t("portal:organizator.hizmet"); } },
];
// Katılımcı / Dekoratör / Taşeron: organizatör bir firmayı organizatör yapamaz (backend yalnız personele açık).
const ESLEME_TIPLERI = [
  { id: 1, get ad() { return i18n.t("portal:organizator.katilimci"); } }, { id: 3, get ad() { return i18n.t("portal:organizator.dekorator"); } }, { id: 4, get ad() { return i18n.t("portal:organizator.taseron"); } },
];
const KART_RENK = { KART_ALABILIR: "success", EVRAK_TAMAM_KART_ALAMAZ: "warning", EVRAK_EKSIK: "danger" };

/** Liste + sayfalama + arama — her sekme aynı iskeleti kullanır. */
// WAS (until #458): function useListe(fetcher, fuarId, ekParams) {  -- ekParams were read when the list was fetched, a change of them did not fetch again
// #458: ekParams are part of what is fetched (ekKey), and the list can start from a search typed earlier (the address)
function useListe(fetcher, fuarId, ekParams, baslangicArama = "") {
  const ekKey = JSON.stringify(ekParams ?? {});
  const [sayfa, setSayfa] = useState(0);
  const [arama, setArama] = useState(baslangicArama.trim());
  const [girdi, setGirdi] = useState(baslangicArama);
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [tetik, setTetik] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => { setArama(girdi.trim()); setSayfa(0); }, 350);
    return () => clearTimeout(t);
  }, [girdi]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setHata(null);
      try {
        const out = await fetcher(fuarId, { ...ekParams, search: arama || undefined, page: sayfa, size: SAYFA });
        if (!ignore) setVeri(out);
      } catch (e) {
        if (!ignore) setHata(hataMetni(e));
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    if (fuarId) run();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // WAS (until #458): }, [fetcher, fuarId, arama, sayfa, tetik]);
  }, [fetcher, fuarId, arama, sayfa, tetik, ekKey]);

  return { veri, yukleniyor, hata, sayfa, setSayfa, girdi, setGirdi, arama, yenile: () => setTetik((t) => t + 1) };
}

function Sayfalama({ toplam, sayfa, setSayfa }) {
  const { t } = useTranslation("portal");
  const sayfaSayisi = Math.max(1, Math.ceil((toplam || 0) / SAYFA));
  if (sayfaSayisi <= 1) return null;
  return (
    <div className="d-flex align-items-center justify-content-between pt-2">
      <span className="text-muted small">{t("organizator.sayfa", { v: sayfa + 1 })}{" "}{sayfaSayisi}</span>
      <Pagination size="sm" className="mb-0">
        <PaginationItem disabled={sayfa === 0}><PaginationLink previous onClick={() => setSayfa(Math.max(0, sayfa - 1))} /></PaginationItem>
        <PaginationItem disabled={sayfa + 1 >= sayfaSayisi}><PaginationLink next onClick={() => setSayfa(Math.min(sayfaSayisi - 1, sayfa + 1))} /></PaginationItem>
      </Pagination>
    </div>
  );
}

function AramaSatiri({ liste, yer, sag }) {
  return (
    <Row className="g-2 mb-2 align-items-center">
      <Col md={5}>
        <Input bsSize="sm" placeholder={yer} value={liste.girdi} onChange={(e) => liste.setGirdi(e.target.value)} />
      </Col>
      <Col className="d-flex justify-content-end gap-2">
        {liste.yukleniyor && <Spinner size="sm" />}
        {sag}
      </Col>
    </Row>
  );
}

/**
 * #418 (2026-10-14): KAPALI organizatör eylemleri ve nedenleri. Eylem ya firmanın rolünde yok (ROLE: rol adı parantezde), ya
 * firmanın seti izin vermiyor (SET_CAP), ya SAKA modülü kısıtladı (COMPANY_RESTRICTION) ya da SAKA bu işlemi firma için açmadı
 * (ORGANIZER_SETTING). Nedeni backend verir (gerekce); metin portalGerekce.js'te. Açık eylemler için hiçbir şey çizmez.
 */
export function KapaliEylemler({ kodlar, izin, kapaliNedeni }) {
  const { t } = useTranslation("portal");
  const satirlar = kodlar.filter((k) => !izin(k)).map((k) => ({ kod: k, neden: kapaliNedeni(k) })).filter((x) => x.neden);
  if (satirlar.length === 0) return null;
  return (
    <div className="small text-muted mb-2" data-testid="kapali-eylemler">
      {satirlar.map((s) => (
        <div key={s.kod} data-eylem={s.kod}><Lock size={12} className="me-1" />{t(`organizator.eylem.${s.kod}`)}: {s.neden}</div>
      ))}
    </div>
  );
}

// ── Standlar ────────────────────────────────────────────────────────────────────────────────
// WAS (until #418): function StandlarSekmesi({ fuarId, izin, bildir }) {
// WAS (until #450): function StandlarSekmesi({ fuarId, izin, kapaliNedeni, bildir }) {  -- salt: the area is at İzleme, no action is drawn
function StandlarSekmesi({ fuarId, izin, kapaliNedeni, bildir, salt }) {
  const { t } = useTranslation("portal");
  const liste = useListe(getOrganizatorStandlar, fuarId);
  const [modal, setModal] = useState(null); // {tip: 'ekle'|'duzenle'|'sil'|'borc', stand}
  const [form, setForm] = useState({});
  const [borc, setBorc] = useState(null);
  const [calisiyor, setCalisiyor] = useState(false);
  const [hata, setHata] = useState(null);
  // #547: sunucu "bu holde bu adla stand var" dedi → Stand Kodu kutusu kırmızı, kod ya da salon değişince söner
  const [kodCakisti, setKodCakisti] = useState(false);

  const kayitlar = useMemo(() => liste.veri?.data ?? [], [liste.veri]);
  const salonlar = useMemo(() => {
    const m = new Map();
    for (const s of kayitlar) if (s.salonId) m.set(s.salonId, s.salonName);
    return [...m.entries()].map(([id, ad]) => ({ id, ad }));
  }, [kayitlar]);

  const ac = async (tip, stand) => {
    setHata(null);
    setKodCakisti(false);
    setForm(tip === "duzenle" ? { standCode: stand.standCode ?? "", tabelaAdi: stand.tabelaAdi ?? "" }
      : tip === "ekle" ? { standCode: "", salonId: salonlar[0]?.id ?? "" } : {});
    setModal({ tip, stand });
    if (tip === "borc") {
      setBorc(null);
      try {
        const d = await api("get", `/standTalep/borcDurumu/${stand.standId}`);
        setBorc(Object.fromEntries((d?.categories ?? []).map((c) => [c.categoryKey, { status: c.status, amount: c.amount ?? "" }])));
      } catch (e) {
        setHata(hataMetni(e));
      }
    }
  };

  const calistir = async (is, mesaj) => {
    setCalisiyor(true);
    setHata(null);
    setKodCakisti(false);
    try {
      await is();
      setModal(null);
      bildir(mesaj);
      liste.yenile();
    } catch (e) {
      setKodCakisti(isStandAdiCakismasi(e));
      setHata(hataMetni(e));
    } finally {
      setCalisiyor(false);
    }
  };

  return (
    <>
      {/* WAS (until #418): sag={izin(STAND_EKLE) && (<Button ...>)} — a closed action vanished without a word */}
      <AramaSatiri liste={liste} yer={t("organizator.standFirmaAra")}
        sag={salt ? null : izin(ORGANIZATOR_AYAR.STAND_EKLE) ? (
          <Button size="sm" color="primary" onClick={() => ac("ekle")}><Plus size={14} className="me-1" />{t("organizator.standEkle")}</Button>
        ) : (
          <span title={kapaliNedeni(ORGANIZATOR_AYAR.STAND_EKLE) || undefined} data-testid="stand-ekle-kapali">
            <Button size="sm" color="primary" disabled><Plus size={14} className="me-1" />{t("organizator.standEkle")}</Button>
          </span>
        )} />
      {!salt && (
        <KapaliEylemler kodlar={[ORGANIZATOR_AYAR.STAND_EKLE, ORGANIZATOR_AYAR.STAND_DUZENLE, ORGANIZATOR_AYAR.STAND_SIL, ORGANIZATOR_AYAR.BORC_YONET]}
          izin={izin} kapaliNedeni={kapaliNedeni} />
      )}
      {liste.hata && <Alert color="danger" className="small">{liste.hata}</Alert>}
      <div className="table-responsive">
        <Table size="sm" className="align-middle mb-0">
          <thead className="table-light">
            <tr><th>{t("organizator.stand")}</th><th>{t("organizator.salon")}</th><th>{t("organizator.katilimci")}</th><th>{t("organizator.dekorator")}</th><th>{t("organizator.durum")}</th>{!salt && <th className="text-end">{t("organizator.islem")}</th>}</tr>
          </thead>
          <tbody>
            {liste.veri && kayitlar.length === 0 && <tr><td colSpan={salt ? 5 : 6} className="text-center text-muted py-3">{t("organizator.standYok")}</td></tr>}
            {kayitlar.map((s) => (
              <tr key={s.standId}>
                {/* WAS (until #548): code on top, stand name as the small line under it */}
                <td>
                  {s.tabelaAdi ? <><div className="fw-semibold">{s.tabelaAdi}</div><div className="small text-muted">{s.standName}</div></> : <span className="fw-semibold">{s.standName}</span>}
                </td>
                <td className="small">{s.salonName}</td>
                <td className="small">{s.katilimciFirmaName || <span className="text-muted">—</span>}</td>
                <td className="small">{s.dekoratorFirmaName || <span className="text-muted">—</span>}</td>
                <td>
                  {s.standIsForbidden && <Badge color="danger" className="me-1">{t("organizator.yasakli")}</Badge>}
                  {s.standIsProblem && <Badge color="warning" className="me-1">{t("organizator.sorunlu")}</Badge>}
                  {(s.waterDebt || s.electricityDebt || s.airDebt || s.internetDebt) && <Badge color="secondary">{t("organizator.borc")}</Badge>}
                </td>
                {!salt && <td className="text-end text-nowrap">
                  {izin(ORGANIZATOR_AYAR.BORC_YONET) && (
                    <Button size="sm" color="link" className="p-1" title={t("organizator.borc")} onClick={() => ac("borc", s)}><Wallet size={15} /></Button>
                  )}
                  {izin(ORGANIZATOR_AYAR.STAND_DUZENLE) && (
                    <Button size="sm" color="link" className="p-1" title={t("common:duzenle")} onClick={() => ac("duzenle", s)}><Pencil size={15} /></Button>
                  )}
                  {izin(ORGANIZATOR_AYAR.STAND_SIL) && (
                    <Button size="sm" color="link" className="p-1 text-danger" title={t("common:sil")} onClick={() => ac("sil", s)}><Trash2 size={15} /></Button>
                  )}
                </td>}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <Sayfalama toplam={liste.veri?.totalCount} sayfa={liste.sayfa} setSayfa={liste.setSayfa} />

      <Modal isOpen={!!modal} toggle={() => !calisiyor && setModal(null)}>
        <ModalHeader toggle={() => !calisiyor && setModal(null)}>
          {modal?.tip === "ekle" && t("organizator.standEkle")}
          {modal?.tip === "duzenle" && t("organizator.standiDuzenle", { standName: modal.stand.standName })}
          {modal?.tip === "sil" && t("organizator.standSilinsinMi")}
          {modal?.tip === "borc" && t("organizator.borc2", { standName: modal.stand.standName })}
        </ModalHeader>
        <ModalBody>
          {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}
          {modal?.tip === "ekle" && (
            <>
              <Label className="small mb-1">{t("organizator.salon")}</Label>
              {salonlar.length > 0 ? (
                <Input type="select" bsSize="sm" className="mb-2" value={form.salonId}
                       onChange={(e) => { setKodCakisti(false); setForm({ ...form, salonId: e.target.value }); }}>
                  {salonlar.map((s) => <option key={s.id} value={s.id}>{s.ad}</option>)}
                </Input>
              ) : (
                <Input bsSize="sm" className="mb-2" placeholder={t("organizator.salonId")} value={form.salonId}
                       onChange={(e) => { setKodCakisti(false); setForm({ ...form, salonId: e.target.value }); }} />
              )}
              <Label className="small mb-1">{t("organizator.standKoduSalonIcindekiOr")}</Label>
              <Input bsSize="sm" value={form.standCode} invalid={kodCakisti}
                     onChange={(e) => { setKodCakisti(false); setForm({ ...form, standCode: e.target.value }); }} />
            </>
          )}
          {modal?.tip === "duzenle" && (
            <>
              <Label className="small mb-1">{t("organizator.standKodu")}</Label>
              <Input bsSize="sm" className="mb-2" value={form.standCode} invalid={kodCakisti}
                     onChange={(e) => { setKodCakisti(false); setForm({ ...form, standCode: e.target.value }); }} />
              <Label className="small mb-1">{t("organizator.tabelaAdi")}</Label>
              <Input bsSize="sm" value={form.tabelaAdi} onChange={(e) => setForm({ ...form, tabelaAdi: e.target.value })} />
            </>
          )}
          {modal?.tip === "sil" && (
            <p className="small mb-0"><strong>{modal.stand.standName}</strong>{" "}{t("organizator.standiListedenKaldirilirKayitSilinmez")}</p>
          )}
          {modal?.tip === "borc" && (borc ? BORC_KATEGORILERI.map((k) => (
            <Row key={k.key} className="g-2 mb-2 align-items-center">
              <Col xs={4} className="small fw-semibold">{k.label}</Col>
              <Col xs={4}>
                <Input type="select" bsSize="sm" value={borc[k.key]?.status ?? "yok"}
                       onChange={(e) => setBorc({ ...borc, [k.key]: { ...borc[k.key], status: e.target.value } })}>
                  {Object.entries(BORC_DURUMU_STATUS).map(([v, d]) => <option key={v} value={v}>{d.label}</option>)}
                </Input>
              </Col>
              <Col xs={4}>
                <Input bsSize="sm" type="number" min={0} placeholder={t("organizator.tutarTl")} disabled={(borc[k.key]?.status ?? "yok") !== "bekliyor"}
                       value={borc[k.key]?.amount ?? ""}
                       onChange={(e) => setBorc({ ...borc, [k.key]: { ...borc[k.key], amount: e.target.value } })} />
              </Col>
            </Row>
          )) : !hata && <Spinner size="sm" />)}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" disabled={calisiyor} onClick={() => setModal(null)}>{t("organizator.vazgec")}</Button>
          {modal?.tip === "ekle" && (
            <Button color="primary" size="sm" disabled={calisiyor || !form.standCode?.trim() || !form.salonId}
                    onClick={() => calistir(() => api("post", "/standTalep/create",
                      { standCode: form.standCode.trim(), fuarId, salonId: Number(form.salonId), firmaEsle: 0 }), "Stand eklendi.")}>
              {t("common:ekle")}
            </Button>
          )}
          {modal?.tip === "duzenle" && (
            <Button color="primary" size="sm" disabled={calisiyor}
                    onClick={() => calistir(() => api("put", `/standTalep/update/${modal.stand.standId}`,
                      { standCode: form.standCode.trim() || undefined, tabelaAdi: form.tabelaAdi }), t("organizator.standGuncellendi"))}>
              {t("common:kaydet")}
            </Button>
          )}
          {modal?.tip === "sil" && (
            <Button color="danger" size="sm" disabled={calisiyor}
                    onClick={() => calistir(() => api("delete", `/standTalep/delete/${modal.stand.standId}`), "Stand silindi.")}>
              {t("common:sil")}
            </Button>
          )}
          {modal?.tip === "borc" && borc && (
            <Button color="primary" size="sm" disabled={calisiyor}
                    onClick={() => calistir(async () => {
                      for (const k of BORC_KATEGORILERI) {
                        const b = borc[k.key] ?? { status: "yok" };
                        const tutar = b.status === "bekliyor" && b.amount !== "" ? Number(b.amount) : null;
                        await setStandBorc(modal.stand.standId, k.key, b.status || "yok", tutar);
                      }
                    }, t("organizator.borcDurumuKaydedildi"))}>
              {t("common:kaydet")}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
}

// ── Firmalar ────────────────────────────────────────────────────────────────────────────────
// WAS (until #418): function FirmalarSekmesi({ fuarId, izin, bildir }) {
// WAS (until #450): function FirmalarSekmesi({ fuarId, izin, kapaliNedeni, bildir }) {
function FirmalarSekmesi({ fuarId, izin, kapaliNedeni, bildir, salt }) {
  const { t: tx } = useTranslation("portal");
  const liste = useListe(getOrganizatorFirmalar, fuarId);
  const [modal, setModal] = useState(null); // {tip: 'esle'|'cikar', firma}
  const [ekle, setEkle] = useState(false);
  const [secim, setSecim] = useState({ firma: null, firmaTypeId: 1 });
  const [calisiyor, setCalisiyor] = useState(false);
  const [hata, setHata] = useState(null);
  const kayitlar = liste.veri?.content ?? [];

  const calistir = async (is, mesaj) => {
    setCalisiyor(true);
    setHata(null);
    try {
      await is();
      setModal(null);
      bildir(mesaj);
      liste.yenile();
    } catch (e) {
      setHata(hataMetni(e));
    } finally {
      setCalisiyor(false);
    }
  };

  return (
    <>
      <AramaSatiri liste={liste} yer={tx("organizator.firmaAra")} sag={salt ? null : (
        <>
          {/* WAS (until #418): both buttons only rendered when allowed; closed ones are shown disabled with the reason */}
          {izin(ORGANIZATOR_AYAR.FIRMA_EKLE) ? (
            <Button size="sm" color="primary" outline onClick={() => setEkle(true)}><Plus size={14} className="me-1" />{tx("organizator.firmaEkle")}</Button>
          ) : (
            <span title={kapaliNedeni(ORGANIZATOR_AYAR.FIRMA_EKLE) || undefined} data-testid="firma-ekle-kapali">
              <Button size="sm" color="primary" outline disabled><Plus size={14} className="me-1" />{tx("organizator.firmaEkle")}</Button>
            </span>
          )}
          {izin(ORGANIZATOR_AYAR.FIRMA_ESLE) ? (
            <Button size="sm" color="primary" onClick={() => { setHata(null); setSecim({ firma: null, firmaTypeId: 1 }); setModal({ tip: "esle" }); }}>
              <Link2 size={14} className="me-1" />{tx("organizator.firmaEsle")}
            </Button>
          ) : (
            <span title={kapaliNedeni(ORGANIZATOR_AYAR.FIRMA_ESLE) || undefined} data-testid="firma-esle-kapali">
              <Button size="sm" color="primary" disabled><Link2 size={14} className="me-1" />{tx("organizator.firmaEsle")}</Button>
            </span>
          )}
        </>
      )} />
      {!salt && <KapaliEylemler kodlar={[ORGANIZATOR_AYAR.FIRMA_EKLE, ORGANIZATOR_AYAR.FIRMA_ESLE, ORGANIZATOR_AYAR.FIRMA_SIL]} izin={izin} kapaliNedeni={kapaliNedeni} />}
      {liste.hata && <Alert color="danger" className="small">{liste.hata}</Alert>}
      <div className="table-responsive">
        <Table size="sm" className="align-middle mb-0">
          <thead className="table-light"><tr><th>{tx("organizator.firma")}</th><th>{tx("organizator.rol")}</th><th>{tx("organizator.standlar")}</th><th>{tx("organizator.dekorator")}</th>{!salt && <th className="text-end">{tx("organizator.islem")}</th>}</tr></thead>
          <tbody>
            {liste.veri && kayitlar.length === 0 && <tr><td colSpan={salt ? 4 : 5} className="text-center text-muted py-3">{tx("organizator.firmaYok")}</td></tr>}
            {kayitlar.map((f) => (
              <tr key={f.firmaId}>
                <td className="fw-semibold">{f.firmaName}{f.firmaNation === 2 && <Badge color="info" className="ms-1">{tx("organizator.yabanci")}</Badge>}</td>
                <td className="small">{f.firmaTypeNames}</td>
                <td className="small">{f.stands || "—"}</td>
                <td className="small">{f.decoratorName || "—"}</td>
                {!salt && <td className="text-end">
                  {izin(ORGANIZATOR_AYAR.FIRMA_SIL) && (
                    <Button size="sm" color="link" className="p-1 text-danger" title={tx("organizator.fuardanCikar")}
                            onClick={() => { setHata(null); setModal({ tip: "cikar", firma: f }); }}>
                      <Ban size={15} />
                    </Button>
                  )}
                </td>}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <Sayfalama toplam={liste.veri?.totalElements} sayfa={liste.sayfa} setSayfa={liste.setSayfa} />

      <Modal isOpen={!!modal} toggle={() => !calisiyor && setModal(null)}>
        <ModalHeader toggle={() => !calisiyor && setModal(null)}>
          {modal?.tip === "esle" ? tx("organizator.firmayiFuaraEsle") : tx("organizator.firmaFuardanCikarilsinMi")}
        </ModalHeader>
        <ModalBody>
          {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}
          {modal?.tip === "esle" && (
            <>
              <Label className="small mb-1">{tx("organizator.firma")}</Label>
              <FirmaSearchSelect forceLive value={secim.firma} onChange={(f) => setSecim({ ...secim, firma: f })} />
              <Label className="small mb-1 mt-2">{tx("organizator.rol")}</Label>
              <Input type="select" bsSize="sm" value={secim.firmaTypeId}
                     onChange={(e) => setSecim({ ...secim, firmaTypeId: Number(e.target.value) })}>
                {ESLEME_TIPLERI.map((t) => <option key={t.id} value={t.id}>{t.ad}</option>)}
              </Input>
            </>
          )}
          {modal?.tip === "cikar" && (
            <p className="small mb-0">
              <strong>{modal.firma.firmaName}</strong>{" "}{tx("organizator.buFuardanCikarilirFuarKaydi")}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" disabled={calisiyor} onClick={() => setModal(null)}>{tx("organizator.vazgec")}</Button>
          {modal?.tip === "esle" && (
            <Button color="primary" size="sm" disabled={calisiyor || !secim.firma?.firmaId}
                    onClick={() => calistir(() => api("post", "/firma/esle",
                      { firmaId: Number(secim.firma.firmaId), fuarId, firmaTypeId: secim.firmaTypeId }), tx("organizator.firmaFuaraEslendi"))}>
              {tx("organizator.esle")}
            </Button>
          )}
          {modal?.tip === "cikar" && (
            <Button color="danger" size="sm" disabled={calisiyor}
                    onClick={() => calistir(() => api("delete", `/firma/${modal.firma.firmaId}/fuar/${fuarId}/soft-delete`), tx("organizator.firmaFuardanCikarildi"))}>
              {tx("organizator.cikar")}
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {ekle && (
        <CreateFirmaModal isOpen forceLive fuarId={fuarId} showEsle={izin(ORGANIZATOR_AYAR.FIRMA_ESLE)} onClose={() => setEkle(false)}
                          onCreated={() => { setEkle(false); bildir("Firma eklendi."); liste.yenile(); }} />
      )}
    </>
  );
}

// ── Talepler ve iptal (#317) ────────────────────────────────────────────────────────────────
// WAS (until #418): function TaleplerSekmesi({ fuarId, izin, bildir }) {
// WAS (until #450): function TaleplerSekmesi({ fuarId, izin, kapaliNedeni, bildir }) {
// WAS (until #456 / #458): function TaleplerSekmesi({ fuarId, izin, kapaliNedeni, bildir, salt }) {  -- no filters, no payment link
function TaleplerSekmesi({ fuarId, firmaId, izin, kapaliNedeni, bildir, salt }) {
  const { t: tx } = useTranslation("portal");
  // #458: the kind chips, the kW range, "above the allowed kW" and the status live in the address (?tur=elektrik&kwMin=..&asan=1&durum=0&ara=..)
  const { filtre, degistir, temizle, apiParams } = useTalepFiltreleri();
  // WAS (until #458): const liste = useListe(getOrganizatorTalepler, fuarId);
  const liste = useListe(getOrganizatorTalepler, fuarId, apiParams, filtre.ara);
  // the search text goes to the address once typing pauses (the list's own debounce), so the page can be bookmarked with it
  const aranan = liste.arama;
  const adresteki = filtre.ara;
  useEffect(() => {
    if (aranan !== adresteki) degistir({ ara: aranan });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aranan]);
  // a changed filter starts again from the first page (the handlers do it, no effect: the filters only change through them)
  const filtreDegistir = (yama) => { liste.setSayfa(0); degistir(yama); };
  const filtreTemizle = () => { liste.setSayfa(0); temizle(); };
  const yerel = currentLocaleTag();
  // #500: the name of a technical request kind (elektrik -> Elektrik); a key it does not know is shown as it is
  const turAdi = (anahtar) => tx(`organizator.talepFiltre.turler.${anahtar}`, { defaultValue: anahtar });
  const [iptal, setIptal] = useState(null); // {standId, tur: 'talep'|'teknik', id|categoryKey, ad}
  const [neden, setNeden] = useState("");
  const [calisiyor, setCalisiyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [gecmisStand, setGecmisStand] = useState(null); // #453: {standId, ad} of the row whose history is open
  const kayitlar = liste.veri?.records ?? [];

  const talepler = (s) => [...(s.hizmet ?? []), ...(s.isIzin ?? []), ...(s.ekipman ? [s.ekipman] : [])]
    .filter((t) => t.hasRequest);

  const iptalEt = async () => {
    setCalisiyor(true);
    setHata(null);
    try {
      if (iptal.tur === "teknik") await teknikTalepGeriCek(iptal.standId, iptal.categoryKey);
      else await talepIptalEt(iptal.standId, iptal.requestId, neden.trim());
      setIptal(null);
      bildir("Talep iptal edildi.");
      liste.yenile();
    } catch (e) {
      setHata(hataMetni(e));
    } finally {
      setCalisiyor(false);
    }
  };

  const durumRenk = (s) => (s === 1 ? "success" : s === 2 ? "danger" : s === 3 ? "secondary" : "warning");

  return (
    <>
      {/* WAS (until #500): <AramaSatiri liste={liste} yer={tx("organizator.standFirmaAra")} /> */}
      <AramaSatiri liste={liste} yer={tx("organizator.standFirmaAra")} sag={<SerbestKwDugmesi fuarId={fuarId} />} />
      <TalepFiltreleri filtre={filtre} degistir={filtreDegistir} temizle={filtreTemizle} />
      {liste.veri?.approximateCount && <div className="small text-muted mb-1" data-testid="talep-yaklasik">{tx("organizator.talepFiltre.yaklasik")}</div>}
      {/* #456: the organizer enters ITS payment link here, only where SAKA turned the action on for the company; at İzleme (salt) nothing about it is drawn */}
      {!salt && izin(ORGANIZATOR_AYAR.ODEME_BAGLANTISI_YONET) && firmaId && (
        <OdemeBaglantisiKarti firmaId={firmaId} fuarId={fuarId} duzenleyebilir onChanged={bildir} />
      )}
      {/* WAS (until #456): kodlar={[TALEP_EKLE, TALEP_SIL]} */}
      {!salt && <KapaliEylemler kodlar={[ORGANIZATOR_AYAR.TALEP_EKLE, ORGANIZATOR_AYAR.TALEP_SIL, ORGANIZATOR_AYAR.ODEME_BAGLANTISI_YONET]} izin={izin} kapaliNedeni={kapaliNedeni} />}
      {liste.hata && <Alert color="danger" className="small">{liste.hata}</Alert>}
      {!salt && <p className="small text-muted">
        {tx("organizator.talepOnayiSakaNinOrganizator")}{izin(ORGANIZATOR_AYAR.TALEP_EKLE) ? "" : tx("organizator.firmanizaAcikDegil")}{" "}{tx("organizator.veIptalEdebilir")}{izin(ORGANIZATOR_AYAR.TALEP_SIL) ? "" : tx("organizator.firmanizaAcikDegil")}{tx("organizator.iptalEdilenTalepSilinmezIptal")}
      </p>}
      <div className="table-responsive">
        <Table size="sm" className="align-middle mb-0">
          <thead className="table-light"><tr><th>{tx("organizator.stand")}</th><th>{tx("organizator.firmalar")}</th><th>{tx("organizator.teknik")}</th><th>{tx("organizator.hizmetIsIzniEkipman")}</th><th>{tx("organizator.talepFiltre.kaynak")}</th><th className="text-end">{tx("organizator.islem")}</th></tr></thead>
          <tbody>
            {liste.veri && kayitlar.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-3">{tx("organizator.talepYok")}</td></tr>}
            {kayitlar.map((s) => (
              <tr key={s.standId}>
                <td className="fw-semibold">{s.standName}<div className="small text-muted">{s.salonName}</div></td>
                <td className="small">{s.katilimciFirmaName || "—"}<div className="text-muted">{s.dekoratorFirmaName}</div></td>
                <td>
                  {(s.teknik ?? []).filter((t) => t.hasRequest).map((t) => (
                    <Badge key={t.categoryKey} color={durumRenk(t.status)} className="me-1 mb-1" pill>
                      {/* WAS (until #500): {t.categoryKey}  -- printed the raw key ("elektrik 15 kW") */}
                      {turAdi(t.categoryKey)}{t.quantity ? ` ${t.quantity.value} kW` : ""}
                      {/* #456: the electricity request is above the kW the fair gives free (the excess is charged): a badge on the row, from kwKontrol */}
                      {t.categoryKey === "elektrik" && kwAsimi(t.kwKontrol, t.quantity?.value).ucretli && (
                        <span className="ms-1 badge bg-warning text-dark" data-testid="asan-rozeti"
                              title={tx("organizator.talepFiltre.asanIpucu", { serbest: kwYazi(t.kwKontrol?.serbestKw, yerel) })}>
                          {tx("organizator.talepFiltre.asanRozeti", { asan: kwYazi(kwAsimi(t.kwKontrol, t.quantity?.value).asan, yerel) })}
                        </span>
                      )}
                      {izin(ORGANIZATOR_AYAR.TALEP_SIL) && (
                        <span role="button" className="ms-1" title={tx("organizator.geriCek")}
                              onClick={() => { setHata(null); setIptal({ standId: s.standId, tur: "teknik", categoryKey: t.categoryKey, ad: turAdi(t.categoryKey) }); }}>×</span>
                      )}
                    </Badge>
                  ))}
                </td>
                <td>
                  {talepler(s).map((t) => (
                    <Badge key={t.requestId ?? t.categoryKey} color={durumRenk(t.status)} className="me-1 mb-1" pill title={t.statusLabel}>
                      {t.categoryName || t.categoryKey || tx("organizator.ekipman")}
                      {izin(ORGANIZATOR_AYAR.TALEP_SIL) && t.requestId && t.status !== 3 && (
                        <span role="button" className="ms-1" title={tx("organizator.iptalEt")}
                              onClick={() => { setHata(null); setNeden(""); setIptal({ standId: s.standId, tur: "talep", requestId: t.requestId, ad: t.categoryName || t.categoryKey }); }}>×</span>
                      )}
                    </Badge>
                  ))}
                </td>
                {/* #462: who entered the requests of this stand (one badge per source; the company's name in its tooltip); an unknown source (before V100) says so */}
                <td data-testid="talep-kaynaklari">
                  {talepKaynaklari(standTalepleri(s)).map((k) => (
                    <TalepKaynagi key={`${k.kaynak ?? "?"}|${k.firmaAdi ?? ""}`} kaynak={k.kaynak} firmaAdi={k.firmaAdi} kisa className="me-1 mb-1" />
                  ))}
                  {standTalepleri(s).length === 0 && <span className="text-muted">—</span>}
                </td>
                {/* WAS (until #453): {!salt && <td className="text-end"> ... </td>}  -- at İzleme the column did not exist; now it holds the history button alone */}
                <td className="text-end text-nowrap">
                  {/* #453: reading the history needs only the right to read the stand's requests, which İzleme has */}
                  <Button size="sm" color="light" className="me-1" title={tx("organizator.gecmis")} aria-label={tx("organizator.gecmis")}
                          data-testid="talep-gecmis-dugme" onClick={() => setGecmisStand({ standId: s.standId, ad: s.standName })}>
                    <GecmisIkonu size={14} />
                  </Button>
                  {/* WAS (until #418): only rendered when allowed */}
                  {!salt && (izin(ORGANIZATOR_AYAR.TALEP_EKLE) ? (
                    <Link to={`/Firma/Talepler/${s.standId}`} className="btn btn-sm btn-outline-primary">{tx("organizator.talepEkle")}</Link>
                  ) : (
                    <span title={kapaliNedeni(ORGANIZATOR_AYAR.TALEP_EKLE) || undefined} data-testid="talep-ekle-kapali">
                      <Button size="sm" color="primary" outline disabled>{tx("organizator.talepEkle")}</Button>
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <Sayfalama toplam={liste.veri?.total} sayfa={liste.sayfa} setSayfa={liste.setSayfa} />

      <Modal isOpen={!!iptal} toggle={() => !calisiyor && setIptal(null)}>
        <ModalHeader toggle={() => !calisiyor && setIptal(null)}>{tx("organizator.talepIptalEdilsinMi")}</ModalHeader>
        <ModalBody>
          {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}
          <p className="small"><strong>{iptal?.ad}</strong>{" "}{tx("organizator.talebi")}{" "}{iptal?.tur === "teknik" ? tx("organizator.geriCekilir") : tx("organizator.iptalDurumunaAlinir")}.</p>
          {iptal?.tur === "talep" && (
            <>
              <Label className="small mb-1">{tx("organizator.gerekceZorunlu")}</Label>
              <Input type="textarea" rows={2} value={neden} maxLength={500} onChange={(e) => setNeden(e.target.value)} />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" disabled={calisiyor} onClick={() => setIptal(null)}>{tx("organizator.vazgec")}</Button>
          <Button color="danger" size="sm" disabled={calisiyor || (iptal?.tur === "talep" && !neden.trim())} onClick={iptalEt}>{tx("organizator.iptalEt")}</Button>
        </ModalFooter>
      </Modal>

      {/* #453: the history of the stand's requests: previous value, new value, when, who */}
      <Modal isOpen={!!gecmisStand} toggle={() => setGecmisStand(null)} size="lg" scrollable>
        <ModalHeader toggle={() => setGecmisStand(null)}>{tx("organizator.talepGecmisiBaslik", { stand: gecmisStand?.ad ?? "" })}</ModalHeader>
        <ModalBody>{gecmisStand && <TalepGecmisi standId={gecmisStand.standId} />}</ModalBody>
      </Modal>
    </>
  );
}

// ── Projeler (yalnızca okuma) ───────────────────────────────────────────────────────────────
function ProjelerSekmesi({ fuarId }) {
  const { t } = useTranslation("portal");
  // listProjects 1 tabanlı sayfa ve `limit` ister; useListe 0 tabanlı gönderir, burada çevrilir.
  const fetcher = useCallback((f, p) => getOrganizatorProjeler(f, { search: p.search, page: p.page + 1, limit: SAYFA }), []);
  const liste = useListe(fetcher, fuarId);
  const kayitlar = liste.veri?.records ?? [];
  return (
    <>
      <AramaSatiri liste={liste} yer={t("organizator.standFirmaAra")} />
      {liste.hata && <Alert color="danger" className="small">{liste.hata}</Alert>}
      <div className="table-responsive">
        <Table size="sm" className="align-middle mb-0">
          <thead className="table-light"><tr><th>{t("organizator.stand")}</th><th>{t("organizator.katilimci")}</th><th>{t("organizator.dekorator")}</th><th>{t("organizator.standTipi")}</th><th>{t("organizator.durum")}</th><th className="text-end">{t("organizator.projeAyrinti")}</th></tr></thead>
          <tbody>
            {liste.veri && kayitlar.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-3">{t("organizator.projeYok")}</td></tr>}
            {kayitlar.map((p) => (
              <tr key={p.id}>
                <td className="fw-semibold">{p.standName}<div className="small text-muted">{p.salonName}</div></td>
                <td className="small">{p.firmaName || "—"}</td>
                <td className="small">{p.dekoratorFirmaName || "—"}</td>
                <td className="small">{p.standTypeName || "—"}</td>
                <td>
                  {p.inRevision && <Badge color="warning" className="me-1">{t("organizator.revizyonda")}</Badge>}
                  <Badge color="light" className="text-muted border">{t("organizator.insaIzni")}{" "}{p.statusId ?? "—"}</Badge>
                </td>
                {/* #451: read-only detail (the details and the file LIST, never the files); every tier that sees the list may open it */}
                <td className="text-end">
                  <Link to={`/Firma/Organizator/Projeler/${p.id}`} className="btn btn-sm btn-outline-primary" data-testid="proje-ayrinti">{t("organizator.projeAyrinti")}</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <Sayfalama toplam={liste.veri?.total} sayfa={liste.sayfa} setSayfa={liste.setSayfa} />
    </>
  );
}

// ── Personel (yalnızca durumlar) ────────────────────────────────────────────────────────────
// WAS (until #448): "Personel", useListe(getOrganizatorPersonel, fuarId) -- the same statuses-only list as "Kişiler"; /personel is still served under the old name
function PersonelSekmesi({ fuarId }) {
  const { t } = useTranslation("portal");
  const liste = useListe(getOrganizatorKisiler, fuarId);
  const kayitlar = liste.veri?.records ?? [];
  return (
    <>
      <AramaSatiri liste={liste} yer={t("organizator.adSoyadFirmaAra")} />
      {liste.hata && <Alert color="danger" className="small">{liste.hata}</Alert>}
      <p className="small text-muted">{t("organizator.yalnizcaBelgeVeKartDurumlari")}</p>
      <div className="table-responsive">
        <Table size="sm" className="align-middle mb-0">
          <thead className="table-light"><tr><th>{t("organizator.kisi")}</th><th>{t("organizator.firma")}</th><th>{t("organizator.meslek")}</th><th>{t("organizator.kart")}</th></tr></thead>
          <tbody>
            {liste.veri && kayitlar.length === 0 && <tr><td colSpan={4} className="text-center text-muted py-3">{t("organizator.kayitYok")}</td></tr>}
            {kayitlar.map((k) => (
              <tr key={k.personId}>
                <td className="fw-semibold">{[k.name, k.surname].filter(Boolean).join(" ")}</td>
                <td className="small">{k.firmaName || (k.firmalar ?? []).map((f) => f.firmaName).join(", ") || "—"}</td>
                <td className="small">{k.meslekName || "—"}</td>
                <td>{k.kartDurumu ? <Badge color={KART_RENK[k.kartDurumu] ?? "secondary"}>{k.kartDurumu.replaceAll("_", " ")}</Badge> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <Sayfalama toplam={liste.veri?.total} sayfa={liste.sayfa} setSayfa={liste.setSayfa} />
    </>
  );
}

// WAS (until #450): ONE page with five tabs (SEKMELER + a default export that drew them); kept below as a comment.
// #450: one AREA = one page and one menu entry; /Firma/Organizator (the old address) goes to the first area the company may see.

/** The areas in menu order, each with its route and its name (portal.json organizator.*). */
const ALAN_SIRASI = [
  ORGANIZATOR_ALAN.STANDLAR, ORGANIZATOR_ALAN.FIRMALAR, ORGANIZATOR_ALAN.TALEPLER, ORGANIZATOR_ALAN.PROJELER, ORGANIZATOR_ALAN.KISILER,
];
const ALAN_ROTASI = {
  STANDLAR: "/Firma/Organizator/Standlar",
  FIRMALAR: "/Firma/Organizator/Firmalar",
  TALEPLER: "/Firma/Organizator/Talepler",
  PROJELER: "/Firma/Organizator/Projeler",
  KISILER: "/Firma/Organizator/Kisiler",
};
const ALAN_ADI_ANAHTARI = {
  STANDLAR: "organizator.standlar", FIRMALAR: "organizator.firmalar", TALEPLER: "organizator.taleplerVeBorc",
  PROJELER: "organizator.projeler", KISILER: "organizator.kisiler", SALONLAR: "organizator.salonlar",
};

/** The organizer set, the fair and the action rules every area page reads. */
function useOrganizatorBaglami() {
  const portal = usePortal();
  const [bilgi, setBilgi] = useState(null);
  const organizator = portal?.arayuzler?.find((a) => a.rol === "ORGANIZATOR") ?? null;
  const izin = useCallback(
    (kod) => Boolean((organizator?.eylemler ?? []).find((e) => e.kod === kod)?.izinli),
    [organizator],
  );
  // #418: why a closed action is closed (backend gerekce -> sentence); null when it is open.
  const kapaliNedeni = useCallback(
    (kod) => (portal?.eylemIpucu ? portal.eylemIpucu(kod, organizator) : null),
    [portal, organizator],
  );
  const bildir = useCallback((m) => { setBilgi(m); setTimeout(() => setBilgi(null), 4000); }, []);
  // #446: the company's tier in one area at this fair (from the organizer set; a staff PREVIEW never audits)
  const kademe = useCallback((alan) => {
    const a = (organizator?.alanlar ?? []).find((x) => x.alan === alan);
    if (!a) return { alan, kademe: "NONE", gorebilir: false, denetleyebilir: false };
    return portal?.onizleme ? { ...a, denetleyebilir: false } : a;
  }, [organizator, portal]);
  return { portal, organizator, fuarId: portal?.fuarId, izin, kapaliNedeni, bildir, bilgi, kademe };
}

/**
 * One area of the organizer panel. `alan` is the area (STANDLAR, FIRMALAR, TALEPLER, PROJELER, KISILER); the page shows its list and, at the
 * Denetim tier only, the actions SAKA allowed. At İzleme it says so and draws no action at all (`salt`).
 */
export function OrganizatorAlanSayfasi({ alan }) {
  const { t } = useTranslation("portal");
  const b = useOrganizatorBaglami();
  useEffect(() => { document.title = i18n.t("portal:organizator.organizatorPaneliFds"); }, []);

  if (!b.organizator) {
    return (
      <div className="page-content"><Container fluid>
        <Alert color="warning" className="mt-3">{t("organizator.firmanizSeciliFuardaOrganizatorDegil")}</Alert>
      </Container></div>
    );
  }
  const k = b.kademe(alan);
  const ad = t(ALAN_ADI_ANAHTARI[alan]);
  if (!k.gorebilir) {
    return (
      <div className="page-content"><Container fluid>
        <Alert color="info" className="mt-3 small" data-testid="alan-kapali">{t("organizator.alanAcikDegil", { alan: ad })}</Alert>
      </Container></div>
    );
  }
  const salt = !k.denetleyebilir;
  // WAS (until #456): const ortak = { fuarId: b.fuarId, izin: ..., salt };  -- firmaId is the organizer company the payment link belongs to
  const ortak = { fuarId: b.fuarId, firmaId: b.portal?.firmaId, izin: b.izin, kapaliNedeni: b.kapaliNedeni, bildir: b.bildir, salt };

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
            <Megaphone size={18} className="text-muted" />
            <h5 className="card-title mb-0 flex-grow-1">{t("organizator.organizatorPaneli")}{" · "}{ad}</h5>
            <Badge color={salt ? "secondary" : "success"} data-testid="alan-kademe">{t(`organizator.kademe.${k.kademe}`)}</Badge>
            <Badge color="primary">{b.organizator.ad}</Badge>
          </CardHeader>
          <CardBody>
            {salt && (
              <Alert color="light" className="small border py-2" data-testid="alan-izleme">{t("organizator.izlemeBilgisi")}</Alert>
            )}
            {b.bilgi && <Alert color="success" className="small py-2">{b.bilgi}</Alert>}
            {alan === ORGANIZATOR_ALAN.STANDLAR && <StandlarSekmesi {...ortak} />}
            {alan === ORGANIZATOR_ALAN.FIRMALAR && <FirmalarSekmesi {...ortak} />}
            {alan === ORGANIZATOR_ALAN.TALEPLER && <TaleplerSekmesi {...ortak} />}
            {alan === ORGANIZATOR_ALAN.PROJELER && <ProjelerSekmesi fuarId={b.fuarId} />}
            {alan === ORGANIZATOR_ALAN.KISILER && <PersonelSekmesi fuarId={b.fuarId} />}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}

export const OrganizatorStandlarPage = () => <OrganizatorAlanSayfasi alan={ORGANIZATOR_ALAN.STANDLAR} />;
export const OrganizatorFirmalarPage = () => <OrganizatorAlanSayfasi alan={ORGANIZATOR_ALAN.FIRMALAR} />;
export const OrganizatorTaleplerPage = () => <OrganizatorAlanSayfasi alan={ORGANIZATOR_ALAN.TALEPLER} />;
export const OrganizatorProjelerPage = () => <OrganizatorAlanSayfasi alan={ORGANIZATOR_ALAN.PROJELER} />;
export const OrganizatorKisilerPage = () => <OrganizatorAlanSayfasi alan={ORGANIZATOR_ALAN.KISILER} />;

/** The old address: the first area the company may see, or the notice that SAKA has opened none. */
export default function OrganizatorPanelPage() {
  const { t } = useTranslation("portal");
  const b = useOrganizatorBaglami();
  useEffect(() => { document.title = i18n.t("portal:organizator.organizatorPaneliFds"); }, []);
  if (!b.organizator) {
    return (
      <div className="page-content"><Container fluid>
        <Alert color="warning" className="mt-3">{t("organizator.firmanizSeciliFuardaOrganizatorDegil")}</Alert>
      </Container></div>
    );
  }
  const ilk = ALAN_SIRASI.find((a) => b.kademe(a).gorebilir);
  if (ilk) return <Navigate to={ALAN_ROTASI[ilk]} replace />;
  return (
    <div className="page-content"><Container fluid>
      <Alert color="info" className="small mt-3" data-testid="alan-hicbiri">{t("organizator.fuarGeneliGorunumFirmanizIcin")}</Alert>
    </Container></div>
  );
}

/* WAS (until #450): the single page with five tabs, kept as it was.
const SEKMELER = [
  { key: "standlar", get ad() { return i18n.t("portal:organizator.standlar"); } },
  { key: "firmalar", get ad() { return i18n.t("portal:organizator.firmalar"); } },
  { key: "talepler", get ad() { return i18n.t("portal:organizator.taleplerVeBorc"); } },
  { key: "projeler", get ad() { return i18n.t("portal:organizator.projeler"); } },
  { key: "personel", get ad() { return i18n.t("portal:organizator.personel"); } },
];

export default function OrganizatorPanelPage() {
  const { t } = useTranslation("portal");
  const portal = usePortal();
  const [sekme, setSekme] = useState("standlar");
  const [bilgi, setBilgi] = useState(null);

  useEffect(() => { document.title = i18n.t("portal:organizator.organizatorPaneliFds"); }, []);

  const organizator = portal?.arayuzler?.find((a) => a.rol === "ORGANIZATOR") ?? null;
  const fuarId = portal?.fuarId;
  const izin = useCallback(
    (kod) => Boolean((organizator?.eylemler ?? []).find((e) => e.kod === kod)?.izinli),
    [organizator],
  );
  // #418: why a closed action is closed (backend gerekce -> sentence); null when it is open.
  const kapaliNedeni = useCallback(
    (kod) => (portal?.eylemIpucu ? portal.eylemIpucu(kod, organizator) : null),
    [portal, organizator],
  );
  const bildir = useCallback((m) => { setBilgi(m); setTimeout(() => setBilgi(null), 4000); }, []);

  if (!organizator) {
    return (
      <div className="page-content"><Container fluid>
        <Alert color="warning" className="mt-3">{t("organizator.firmanizSeciliFuardaOrganizatorDegil")}</Alert>
      </Container></div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
            <Megaphone size={18} className="text-muted" />
            <h5 className="card-title mb-0 flex-grow-1">{t("organizator.organizatorPaneli")}</h5>
            <Badge color="primary">{organizator.ad}</Badge>
          </CardHeader>
          <CardBody>
            {!organizator.fuarGorunumu ? (
              <Alert color="info" className="small mb-0">
                {t("organizator.fuarGeneliGorunumFirmanizIcin")}
              </Alert>
            ) : (
              <>
                {bilgi && <Alert color="success" className="small py-2">{bilgi}</Alert>}
                <Nav tabs className="mb-3">
                  {SEKMELER.map((s) => (
                    <NavItem key={s.key}>
                      <NavLink href="#" active={sekme === s.key} onClick={(e) => { e.preventDefault(); setSekme(s.key); }}>{s.ad}</NavLink>
                    </NavItem>
                  ))}
                </Nav>
                {sekme === "standlar" && <StandlarSekmesi fuarId={fuarId} izin={izin} kapaliNedeni={kapaliNedeni} bildir={bildir} />}
                {sekme === "firmalar" && <FirmalarSekmesi fuarId={fuarId} izin={izin} kapaliNedeni={kapaliNedeni} bildir={bildir} />}
                {sekme === "talepler" && <TaleplerSekmesi fuarId={fuarId} izin={izin} kapaliNedeni={kapaliNedeni} bildir={bildir} />}
                {sekme === "projeler" && <ProjelerSekmesi fuarId={fuarId} />}
                {sekme === "personel" && <PersonelSekmesi fuarId={fuarId} />}
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}
*/
